/* eslint-disable no-nested-ternary */
// @mui material components
import Card from "@mui/material/Card";
import momentTz from "moment-timezone";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { CircularProgress, Stack } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
// import BgWorkspace from "assets/images/bg-workspace.png";
import useAuth from "auth/useAuth";
import { loginUserAction } from "redux/slice/users";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { enqueueSnackbar } from "notistack";
import { getAPICall } from "lib/callApi";
import { WORKSPACE_DROPDOWN_LISTS, GET_USER_WORKSPACES } from "lib/constants";

import CoverLayout from "../components/CoverLayout";
import CompanyDetailsForm from "./Steps/CompanyDetailsForm";
import UserDetailsForm from "./Steps/UserDetailsForm";

function WorkspaceForm({
  selectedTeam,
  setShowWorkSpaceForm,
  setSelectedTeam,
  hasTeams,
  isInvitedUser,
  inviteCode,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(isInvitedUser ? 1 : 0);
  const [image, setImage] = useState();
  const [profilePicture, setProfilePicture] = useState("");
  const [workspaceDropdownFieldsFetchLoading, setWorkspaceDropdownFieldsFetchLoading] =
    useState(true);

  const [rolesList, setRolesList] = useState([]);
  const [industriesList, setIndustriesList] = useState([]);
  const [timeAtCompanyOptions, setTimeAtCompanyOptions] = useState([]);
  const [averageSalesCycleLengthOptions, setAverageSalesCycleLengthOptions] = useState([]);
  const [userWorkspaces, setUserWorkspaces] = useState([]);

  const validationSchema = [
    Yup.object({
      companyName: Yup.string()
        .max(100, "Company name cannot be more than 100 characters")
        .required("Company name is required"),
      industry: Yup.object({
        value: Yup.string().required("Industry is required"),
      }).typeError("Industry is required"),
    }),
    Yup.object({
      name: Yup.string()
        .max(100, "Name cannot be more than 100 characters")
        .required("Name is required"),
      timeAtCompany: Yup.object({
        value: Yup.string().required("Time at company is required"),
      }).typeError("Time at company is required"),
      averageSalesCycleLength: Yup.object({
        value: Yup.string().required("Average sales cycle length is required"),
      }).typeError("Average sales cycle length is required"),
      ...(selectedTeam
        ? {}
        : {
            role: Yup.object({
              value: Yup.string().required("Role is required"),
            }).typeError("Role is required"),
          }),
    }),
  ];

  const steps = ["Company details", "User details"];
  const currentValidationSchema = validationSchema[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  const loginUser = useSelector((state) => state.loginUser.loginUserState);

  const {
    user,
    registerWorkspace,
    joinExistingWorkspace,
    joinInvitedUserToWorkspace,
    isLoading,
    error,
  } = useAuth();

  const [initialValues, setInitialValues] = useState({
    companyName: selectedTeam?.name,
    industry: selectedTeam?.FK_company_industries_id
      ? industriesList?.find(
          (option) => option?.value === selectedTeam.FK_company_industries_id
        ) || { value: selectedTeam?.FK_company_industries_id }
      : null,
    name: "",
    role: null,
    timeAtCompany: null,
    averageSalesCycleLength: null,
  });

  const fetchWorkspaceDropdownsLists = async () => {
    setWorkspaceDropdownFieldsFetchLoading(true);
    try {
      const allDropDownLists = await getAPICall(WORKSPACE_DROPDOWN_LISTS, true);
      if (allDropDownLists?.data?.success) {
        setRolesList(allDropDownLists.data?.data?.roles || []);
        setIndustriesList(allDropDownLists.data?.data?.industries || []);
        setTimeAtCompanyOptions(allDropDownLists.data?.data?.timeAtCompany || []);
        setAverageSalesCycleLengthOptions(
          allDropDownLists.data?.data?.averageSalesCycleLength || []
        );
      } else if (allDropDownLists.response.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: allDropDownLists.response.data.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setWorkspaceDropdownFieldsFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspaceDropdownsLists();
  }, []);

  useEffect(() => {
    setInitialValues({});
  }, [isInvitedUser]);

  const fetchUserWorkspaces = async () => {
    const result = await getAPICall(GET_USER_WORKSPACES, true, {
      includeDetails: true,
    });

    if (result?.data) {
      setUserWorkspaces(result?.data || []);
    }
  };

  useEffect(() => {
    if (user.id) fetchUserWorkspaces();
  }, [user.id]);

  useEffect(() => {
    if (workspaceDropdownFieldsFetchLoading) return;

    const userCompanyLink = userWorkspaces?.find(
      (userCompaniesLinks) =>
        (selectedTeam?.id ? selectedTeam.id === userCompaniesLinks.workspaceId : true) &&
        (isInvitedUser
          ? userCompaniesLinks.FK_user_workspace_statuses_status === "invite_sent"
          : userCompaniesLinks.FK_user_workspace_statuses_status === "active")
    );

    setInitialValues({
      companyName: selectedTeam?.name,
      industry: selectedTeam?.FK_company_industries_id
        ? industriesList?.find(
            (option) => option?.value === selectedTeam.FK_company_industries_id
          ) || { value: selectedTeam?.FK_company_industries_id }
        : null,
      name: user.name || "",
      role: null,
      timeAtCompany: userCompanyLink?.FK_user_company_tenure_years_id
        ? timeAtCompanyOptions?.find(
            (option) => option?.value === userCompanyLink.FK_user_company_tenure_years_id
          ) || {
            value: userCompanyLink.FK_user_company_tenure_years_id,
          }
        : null,
      averageSalesCycleLength: userCompanyLink?.FK_user_sales_cycles_id
        ? averageSalesCycleLengthOptions?.find(
            (option) => option?.value === userCompanyLink.FK_user_sales_cycles_id
          ) || {
            value: userCompanyLink.FK_user_sales_cycles_id,
          }
        : null,
    });

    if (isInvitedUser) {
      setActiveStep(1);
    }
  }, [
    user,
    userWorkspaces,
    selectedTeam,
    isInvitedUser,
    industriesList,
    timeAtCompanyOptions,
    averageSalesCycleLengthOptions,
    workspaceDropdownFieldsFetchLoading,
  ]);

  useEffect(() => {
    if (user.passwordToken) {
      const append = selectedTeam?.id ? "?redirect=workspace-selection" : "";
      dispatch(
        loginUserAction.getLoginUserData({
          ...loginUser,
          ...user,
        })
      );
      navigate(`/create-password/${user.passwordToken}${append}`, { replace: true });
    }
  }, [user, selectedTeam]);

  const submitForm = async (data) => {
    const dataToPass = {
      timezone: momentTz.tz.guess(),
      companyName: data.companyName,
      name: data.name,
      role: data?.role?.value,
      email: loginUser.email,
      industry: data?.industry?.value,
      timeAtCompany: data.timeAtCompany.value,
      averageSalesCycleLength: data.averageSalesCycleLength.value,
      ...(profilePicture && image
        ? {
            profilePicture: {
              image: profilePicture,
              name: `${loginUser.email.split(" ").join("-")}.${
                image.type.split("/")[1]
              }`.toLowerCase(),
              type: image.type,
            },
          }
        : {}),
    };
    if (selectedTeam?.id) {
      if (isInvitedUser)
        joinInvitedUserToWorkspace({ ...dataToPass, companyId: selectedTeam.id, inviteCode });
      else joinExistingWorkspace({ ...dataToPass, companyId: selectedTeam.id });
    } else {
      registerWorkspace(dataToPass);
    }
  };

  const handleSubmit = async (data, actions) => {
    if (isLastStep) {
      submitForm(data);
    } else {
      actions.setTouched({});
      actions.setErrors({});
      actions.setSubmitting(false);
      setActiveStep(activeStep + 1);
    }
  };

  function handleBack(values) {
    setActiveStep(activeStep - 1);
    setInitialValues({ ...values });
  }

  const renderStepContent = (step, formProps) => {
    switch (step) {
      case 0:
        return (
          <CompanyDetailsForm
            formProps={formProps}
            selectedTeam={selectedTeam}
            industriesList={industriesList}
          />
        );
      case 1:
        return (
          <UserDetailsForm
            formProps={formProps}
            selectedTeam={selectedTeam}
            setImage={setImage}
            profilePicture={profilePicture}
            setProfilePicture={setProfilePicture}
            rolesList={rolesList}
            timeAtCompanyOptions={timeAtCompanyOptions}
            averageSalesCycleLengthOptions={averageSalesCycleLengthOptions}
          />
        );
      default:
        return <div>Not Found</div>;
    }
  };

  return (
    <CoverLayout
      justifyContent="end"
      pageLayoutStyle={{
        // backgroundImage: `url(${BgWorkspace})`,
        // backgroundSize: "cover",
        // backgroundRepeat: "no-repeat",
        // backgroundPosition: "center",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "black",
      }}
      skipCover
      gridItemStyle={{ minWidth: { xs: "90vw", md: "50vw" } }}
    >
      <MDBox
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <MDBox sx={{ minWidth: { xs: "100%", md: "75%", lg: "60%" } }}>
          {isLoading || workspaceDropdownFieldsFetchLoading ? (
            <Stack alignItems="center">
              <CircularProgress
                sx={{
                  position: "fixed",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 2,
                }}
                color="info"
                size="10vh"
              />
            </Stack>
          ) : (
            ""
          )}
          <Card>
            <MDBox
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="secondary"
              mx={2}
              mt={-3}
              py={2}
              mb={1}
              textAlign="center"
            >
              <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                {selectedTeam?.id ? "Enter your name" : "Create an account"}
              </MDTypography>
            </MDBox>
            <MDBox pt={4} pb={3} px={3}>
              <Formik
                enableReinitialize
                validateOnChange
                validationSchema={currentValidationSchema}
                initialValues={initialValues}
                onSubmit={handleSubmit}
              >
                {({ values, setFieldValue, touched, errors }) => (
                  <Form>
                    {user?.disabled ? (
                      <MDBox mb={2}>
                        <MDTypography variant="body2">
                          You&apos;re no longer on a company plan, you can create a free single user
                          license or
                          <br />
                          contact your company administrator to purchase a company plan
                        </MDTypography>
                      </MDBox>
                    ) : null}

                    {!isInvitedUser && (
                      <MDBox mb={2}>
                        {activeStep ? (
                          <ArrowBack
                            style={{ cursor: "pointer" }}
                            onClick={() => handleBack(values)}
                          />
                        ) : null}
                      </MDBox>
                    )}

                    {renderStepContent(activeStep, {
                      values,
                      setFieldValue,
                      touched,
                      errors,
                    })}

                    {error ? (
                      <span style={{ color: "#d32f2f", fontSize: "12px" }}>{error}</span>
                    ) : null}
                    <MDBox mt={4} mb={1}>
                      <MDButton color="primary" fullWidth type="submit">
                        {isLastStep
                          ? selectedTeam?.id
                            ? isInvitedUser
                              ? "Join Now"
                              : "Request Access"
                            : "Create"
                          : "Next"}
                      </MDButton>
                    </MDBox>
                    {hasTeams && !isInvitedUser && (
                      <MDBox mt={4} mb={1}>
                        <MDButton
                          variant="gradient"
                          color="newColor"
                          fullWidth
                          onClick={() => {
                            setShowWorkSpaceForm(false);
                            setSelectedTeam(null);
                          }}
                        >
                          Go back
                        </MDButton>
                      </MDBox>
                    )}
                  </Form>
                )}
              </Formik>
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
    </CoverLayout>
  );
}

export default WorkspaceForm;
